import { useEffect, useMemo } from 'react';
import type { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { Proxy } from '#~/agent/types.js';
import { useAnalytics } from '#~/analytics/index.js';
import { createAlertTracker } from '#~/analytics/domains/alert-lifecycle.js';
import { depot } from '#~/depot/index.js';
import {
  getIncident,
  raise,
  resolve,
  subscribeIncidents,
  supersede,
  toIncidentKey
} from '#~/notifications/incidents.js';
import { useFeatureFlag } from '#~/posthog/index.js';
import { useMultiProxyStateQuery } from '#~/statequery/hook.js';

const GB = 1_000_000_000;
const SIX_HOURS = 6 * 60 * 60 * 1000;
const selectDisk = (state: T_Maybe<State>) => ({
  free: state?.status?.encoder?.diskFreeSpace,
  total: state?.status?.encoder?.diskTotalSpace,
  limit: state?.status?.encoder?.diskRecordingStopLimit
});
type Disk = ReturnType<typeof selectDisk>;
const formatGB = (bytes: number): string => String(Math.round((bytes / GB) * 100) / 100);

/** Warn every Studio participant using the Bridge's published recording-volume figures. */
export const useLowDiskNotifications = ({
  id,
  roomId,
  name
}: Pick<Proxy, 'id' | 'roomId' | 'name'>): void => {
  const disks = useMultiProxyStateQuery<Disk>(selectDisk);
  const { free, total, limit } = disks[id] ?? {};
  const { enabled, loading } = useFeatureFlag('alert_n4_enabled', { defaultValue: true });
  const { track } = useAnalytics();
  const trackLatest = useLatestFn(track);
  const tracker = useMemo(() => createAlertTracker(trackLatest), [trackLatest]);
  const key = useMemo(() => ({ alert: 'n4' as const, entity: id, audience: roomId }), [id, roomId]);

  useEffect(() => {
    const unsubscribe = subscribeIncidents((event) => {
      if (event.incident.key !== toIncidentKey(key)) return;
      const showing = {
        alert_id: key.alert,
        showing_id: event.incident.showingId,
        agent_id: id,
        studio_id: roomId
      };
      if (event.type === 'shown') tracker.trackAlertShown(showing);
      if (event.type === 'closed') tracker.trackAlertClosed(showing, event.reason);
    });
    return () => {
      // Leaving this studio is not evidence that the Bridge has recovered.
      supersede(key);
      unsubscribe();
    };
  }, [id, roomId, key, tracker]);

  useEffect(() => {
    if (loading || !enabled) {
      supersede(key);
      return;
    }
    // Old firmware omits total/limit; failed measurements are -1.
    // Unknown data proves neither low space nor recovery.
    if (
      free === undefined ||
      total === undefined ||
      limit === undefined ||
      !Number.isFinite(free) ||
      !Number.isFinite(total) ||
      !Number.isFinite(limit) ||
      free < 0 ||
      total <= 0 ||
      limit < 0 ||
      free > total
    )
      return;

    const threshold = Math.max(10 * GB, total * 0.1);
    const message = `${name ?? `${vendorName()} Bridge`} has ${formatGB(free)} GB left. Recording will stop when it reaches ${formatGB(limit)} GB.`;
    let notificationId = getIncident(key)?.notificationId;
    const refreshMessage = (): void => {
      if (!notificationId) return;
      const entry = depot.getState().notifications[notificationId];
      if (!entry || entry[2].isClosed || entry[0].message === message) return;
      // Inspect the open entry first: getIncident refreshes closed entries to
      // unknown, which would interfere with the store's pending close event.
      const incident = getIncident(key);
      if (incident?.state !== 'showing') return;
      // Preserve this showing's lifecycle, timestamp and read state. Re-posting
      // would reopen a dismissed notice and can cancel its pending shown event.
      depot.getState().produce((draft) => {
        draft.notifications[incident.notificationId]![0].message = message;
      });
    };
    // Delivery is deferred by the store. Also refresh at delivery so a reading
    // that arrives before the first post is not lost; never create a notice here.
    const unsubscribe = depot.subscribe((state, previous) => {
      if (state.notifications !== previous.notifications) refreshMessage();
    });
    refreshMessage();
    const check = (): void => {
      if (free >= threshold + GB) {
        resolve(key, { resetCooldown: true });
      } else if (free <= threshold) {
        const incident = raise(
          key,
          {
            title: 'Running low on space',
            message,
            severity: 'warning'
          },
          { cooldown: SIX_HOURS, cooldownFrom: 'dismissed', autoClose: false, announce: 'polite' }
        );
        notificationId = incident?.notificationId ?? notificationId;
      }
    };
    check();
    // State-query snapshots may stay identical for hours. Recheck without
    // duplicating the incident helper's cooldown or dismissal state locally.
    const timer = setInterval(check, 60_000);
    return () => {
      clearInterval(timer);
      unsubscribe();
    };
  }, [free, total, limit, enabled, loading, key, name]);
};
